import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import AxisHelper, { cleanCollisions } from '../chart_helpers/AxisHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import * as COLORS from '../utils/colorUtils'

import { color as d3_color } from 'd3-color'
import { ticks as d3_ticks } from 'd3-array'
import { extent as d3_extent } from 'd3-array'
import {
  curveLinear as d3_curveLinear,
  line as d3_line,
  curveLinearClosed as d3_curveLinearClosed,
  lineRadial as d3_lineRadial,
} from 'd3-shape'

import SizeHelper from '../chart_helpers/SizeHelper'
export default class ParallelCoordsChart extends BaseChart {
  constructor(...args) {
    super(...args)
  }
  static defaultConfig = {
    /**
     * Show/hide the data points that form each line.
     */
    showPoints: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    AxisHelper: {
      x: {
        scale: 'point',
        getExtent: function (data) {
          return data[0] ? Object.keys(data[0].dimensions).map(d => d) : []
        },
        showTickValues: {
          exposed: false,
          nonEditable: true,
        },
      },
      y: {
        scale: {
          value: ['linear'],
          multiValueIds: 'data.dimensions',
        },
        normalized: {
          type: Boolean,
          default: true,
          exposed: true,
        },
        getExtent: function (data, dimLabel, dimId) {
          if (this.config.AxisHelper.y.normalized) {
            let dimNames = this.getYAxesLabels()
            let extents = dimNames.map(dimLabel =>
              this.data.reduce((res, d) => res.concat(d.dimensions[dimLabel]), [])
            )
            let extent = d3_extent(extents.flat())
            return extent
          } else {
            let extent = d3_extent(data.reduce((res, d) => res.concat(d.dimensions[dimLabel]), []))
            let yDims = this.getYAxesIds()
            if (this.getUnit(dimId[0]) && this.getUnit(dimId[0])[yDims.indexOf(dimId)]) {
              extent = cleanCollisions(
                this.axes[dimId],
                extent,
                [{ value: extent.map(e => ({ value: e })) }],
                1.01
              )
            }
            return extent
          }
        },
        valueFormat: {
          value: ['[N].2s'],
          multiValueIds: 'data.dimensions',
        },
        unit: {
          value: [false],
          multiValueIds: 'data.dimensions',
        },
        dimensions: 'data.dimensions',
      },
    },
    TooltipBehavior: {
      getHtml: function (d) {
        let yDims = this.getYAxesIds()
        let yDimNames = this.getYAxesLabels()
        let onAxis = yDims[yDimNames.indexOf(d.id)]
        return `
        <div class="tip-value important">${this.valueWithUnit(d.value, onAxis)}</div>
        <div class="tip-id">
          <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
            { id: d.parentId }
          )}; margin-right: 3px"></span>
          ${d.parentId}
        </div>
        <div class="secondary">${d.id}</div>
        `
      },
    },
  }
  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper, AxisHelper]
  checkConsistency() {
    return true
  }
  setSelectedFunc(sel, d) {
    if (!this.axes.x) {
      // let c = d3_color(this.color(d));
      // c.opacity = 0.4
      // sel.select('path').attr('fill',c+'')
      sel.select('path').attr('fill', this.color(d))
      sel.select('path').attr('fill-opacity', 0.4)
    }
  }
  unsetSelectedFunc(sel) {
    if (!this.axes.x) {
      // sel.select('path').attr('fill','rgba(0,0,0,0)')
      sel.select('path').attr('fill', 'none')
      sel.select('path').attr('fill-opacity', 0)
    }
  }
  draw() {
    let dimsIds = this.data[0] ? Object.keys(this.data[0].dimensions).map((d, i) => 'y_' + i) : []
    let dimsNames = this.data[0] ? Object.keys(this.data[0].dimensions).map(d => d) : []
    let line
    let getValues
    let getPos
    if (this.axes.x) {
      getValues = (dim, value, i) => {
        return [this.axes.x.func.scale()(dim), this.axes[dimsIds[i]].func.scale()(value)]
      }
      getPos = ([x, y]) => {
        return [x, y]
      }
      line = d => {
        return d3_line().curve(d3_curveLinear)(
          dimsNames.map((dim, i) => {
            // return [this.axes.x.scale(dim), this.axes[dimsIds[i]].scale(d.dimensions[dim])]
            return getValues(dim, d.dimensions[dim], i)
          })
        )
      }
    } else {
      getValues = (dim, value, i) => {
        return [(i / dimsIds.length) * 2 * Math.PI, this.axes[dimsIds[i]].func.scale()(value)]
      }
      getPos = ([angle, radius]) => {
        return [Math.cos(angle - Math.PI / 2) * radius, Math.sin(angle - Math.PI / 2) * radius]
      }
      line = d => {
        return d3_lineRadial().curve(d3_curveLinearClosed)(
          dimsNames.map((dim, i) => {
            // return [(i / dimsIds.length) * 2*Math.PI, this.axes[dimsIds[i]].scale(d.dimensions[dim])]
            return getValues(dim, d.dimensions[dim], i)
          })
        )
      }
    }
    this.parentItemsEnter.append('path').attr('fill', 'none').attr('pointer-events', 'none')

    let self = this
    this.parentItemsEnter
      .select('path')
      .attr('d', d => line(d))
      // .attr('fill','rgba(0,0,0,0)')
      .attr('stroke', d => this.color(d))
      .attr('stroke-width', d => this.thickness(d))
      // .attr('stroke-dasharray',(d)=>this.strokeDasharray(d))
      .each(function () {
        this._totalLength = this.getTotalLength()
      })
      .attr('stroke-dasharray', function (d) {
        var dashing = self.strokeDasharray(d)
        if (dashing === 'none') dashing = '1 0'
        var dashLength = dashing
          .split(/[\s,]/)
          .map(function (a) {
            return parseFloat(a) || 0
          })
          .reduce(function (a, b) {
            return a + b
          })
        var dashCount = Math.ceil(this._totalLength / dashLength)
        var newDashes = new Array(dashCount).join(dashing + ' ')
        var dashArray = newDashes + ' 0, ' + this._totalLength
        return dashArray
      })
      .attr('stroke-dashoffset', function () {
        return this._totalLength
      })

    this.parentItemsEnterTrans.select('path').attr('stroke-dashoffset', 0)

    this.parentItemsTrans
      .select('path')
      .attr('d', d => line(d))
      .attr('stroke', d => this.color(d))
      .attr('stroke-dasharray', d => this.strokeDasharray(d))
      //hardcoding width to 2
      .attr('stroke-width', 2)

    // let self = this
    let items = this.items.merge(this.itemsEnter)
    let breakpoints = items
      .raise()
      .selectAll('.break-point')
      .data(this.config.showPoints ? [null] : [])
    breakpoints.exit().remove()
    let breakpointsEnter = breakpoints.enter().append('g').attr('class', 'break-point')
    breakpointsEnter.append('circle')

    this.itemsEnter
      .select('.break-point')
      .attr('transform', function (d, i) {
        let pos = getPos(getValues(d.id, d.value, i))
        return `translate(${pos[0]},${pos[1]})`
        // return `translate(${self.axes.x.scale(d.id)},${self.axes[dimsIds[i]].scale(d.value)})`
      })
      .select('circle')
      .attr('fill', d =>
        this.config.skin === 'light' ? COLORS.SKIN_BACKGROUND : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('stroke', d => this.color({ id: d.parentId }))
      .attr('r', 0)

    this.itemsEnterTrans
      .select('.break-point')
      .select('circle')
      .attr(
        'fill',
        this.config.skin === 'light' ? COLORS.SKIN_BACKGROUND : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('stroke', d => this.color({ id: d.parentId }))
      .attr('r', 3)

    this.itemsTrans
      .select('.break-point')
      .attr('transform', function (d, i) {
        let pos = getPos(getValues(d.id, d.value, i))
        return `translate(${pos[0]},${pos[1]})`
        // return `translate(${self.axes.x.scale(d.id)},${self.axes[dimsIds[i]].scale(d.value)})`
      })
      .select('circle')
      .attr(
        'fill',
        this.config.skin === 'light' ? COLORS.SKIN_BACKGROUND : COLORS.SKIN_BACKGROUND_DARK
      )
      .attr('stroke', d => this.color({ id: d.parentId }))
      .attr('r', 3)

    this.itemsExitTrans.style('opacity', 0)
    if (this.parentItemsExitTrans) this.parentItemsExitTrans.style('opacity', 0)

    //(RADAR) AXIS LABELS
    let xDims = this.getXAxesIds()
    let yDims = this.getYAxesIds()
    if (xDims.length === 0) {
      let yDimNames = this.getYAxesLabels()
      let titleRadius = this.axes[yDims[0]].scale.range()[1] + 25
      yDims.forEach((axis, i) => {
        let root = this.axes[axis].selection
        let titles = root.selectAll('.title').data([null])
        let titlesEnter = titles.enter().append('text').attr('class', 'title')
        let angle = (i / yDims.length) * 360
        titles
          .merge(titlesEnter)
          .text(d => yDimNames[i])
          .attr('text-anchor', () => (angle === 0 ? 'middle' : angle > 180 ? 'end' : 'start'))
          .attr('dominant-baseline', 'central')
          .attr('fill', 'currentColor')
          .attr('transform', d => `translate(0,${titleRadius}) rotate(${-angle + 180}) `)
      })
    }

    //(RADAR) Grid lines
    if (xDims.length === 0) {
      let mainAxis = this.axes['y_0']
      let yDims = this.getYAxesIds()
      // let dimNames = this.getYAxesLabels()
      let extents = yDims.map(dimId =>
        this.data.reduce((res, d) => res.concat(this.axes[dimId].scale.domain()), [])
      )
      let domain = d3_extent(extents.flat())
      // SINO esta noramlized, no tienen sentido los "grids"
      let gridTicks = this.config.AxisHelper.y.normalized ? d3_ticks(domain[0], domain[1], 5) : []
      // console.log('gridticks', domain, gridTicks)
      let grids = mainAxis.selection.selectAll('.grid').data(gridTicks, d => d)
      let gridsEnter = grids.enter().append('path').attr('class', 'grid').attr('stroke', '#ddd')
      grids.exit().remove()
      grids = grids.merge(gridsEnter)
      let dtheta = (2 * Math.PI) / yDims.length
      grids.attr('d', d => {
        let r = mainAxis.func.scale()(d)
        return (
          `M 0,${r}` +
          yDims
            .map((dim, i) => `L ${Math.sin(dtheta * i) * r} ${Math.cos(dtheta * i) * r}`)
            .join('') +
          'Z'
        )
      })
      // NO LOS QUIEREN!
      // console.log('main domain', mainAxis.scale.domain())
      // let ticks = mainAxis.selection.selectAll('.my-tick').data(mainAxis.scale.domain())
      // let ticksEnter = ticks
      //   .enter()
      //   .append('text')
      //   .attr('class', 'my-tick')
      //   .attr('fill', 'currentColor')
      //   .attr('text-anchor', 'start')
      //   .attr('font-size', '14px')
      // ticks
      //   .merge(ticksEnter)
      //   .text(d => {
      //     return this.formatValue(d, 'y_0')
      //   })

      //   .attr('dx', 5)
      //   .attr('transform', d => ` translate(0,${mainAxis.func.scale()(d)}) rotate(${-180})`)
    }
  }
}
